import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, uploadImage, buildFileUrl } from "../lib/api";
import { usePubSocket } from "../lib/usePubSocket";
import { useAuth } from "../context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import {
  Loader2,
  Megaphone,
  Flame,
  LogOut,
  Sparkles,
  Crown,
  ImagePlus,
  CheckCircle2,
  X as XIcon,
} from "lucide-react";
import { DhunLogo, AivigoFooter } from "../components/DhunLogo";
import VibePieChart from "../components/VibePieChart";
import PaymentDialog from "../components/PaymentDialog";

const POLL_MS = 15000;

const OCCASIONS = ["Birthday", "Anniversary", "Dating Anniversary", "In Memory Of"];

export default function MainPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, logout, loading } = useAuth();

  const [pub, setPub] = useState(null);
  const [tab, setTab] = useState("shoutouts");
  const [packages, setPackages] = useState([]);
  const [vibe, setVibe] = useState({ options: [], total_votes: 0, leader: null, user_vote: null });
  const [table] = useState(() => localStorage.getItem(`dd_table_${slug}`) || null);

  // Shout-out state
  const [selectedPkg, setSelectedPkg] = useState(null);
  const [occasion, setOccasion] = useState(null);
  const [senderName, setSenderName] = useState("");
  const [dedicatedTo, setDedicatedTo] = useState("");
  const [message, setMessage] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploadedPhoto, setUploadedPhoto] = useState(null); // {path, url}
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [shoutoutSongText, setShoutoutSongText] = useState("");
  const [payOpen, setPayOpen] = useState(false);

  // Bidding state
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!loading && !user) navigate(`/pub/${slug}`);
  }, [loading, user, navigate, slug]);

  useEffect(() => {
    if (user?.name) setSenderName(user.name);
  }, [user]);

  const loadPub = useCallback(async () => {
    try {
      const { data } = await api.get(`/pubs/${slug}`);
      setPub(data);
    } catch {
      toast.error("Pub not found");
    }
  }, [slug]);

  const loadAll = useCallback(async () => {
    if (!pub) return;
    try {
      const [p, v] = await Promise.all([
        api.get(`/shoutout/packages?pub_id=${pub.id}`),
        api.get(`/vibe?pub_id=${pub.id}`),
      ]);
      setPackages(p.data);
      setVibe(v.data);
    } catch (e) {
      // keep silent on polling errors
    }
  }, [pub]);

  useEffect(() => {
    loadPub();
  }, [loadPub]);

  useEffect(() => {
    if (!pub) return;
    loadAll();
    const t = setInterval(loadAll, POLL_MS);
    return () => clearInterval(t);
  }, [pub, loadAll]);

  usePubSocket(pub?.id, useCallback((msg) => {
    if (!pub) return;
    if (msg.type === "packages_update") {
      api.get(`/shoutout/packages?pub_id=${pub.id}`).then((r) => setPackages(r.data)).catch(() => {});
    }
    if (["vibe_update", "vibe_reset"].includes(msg.type)) {
      api.get(`/vibe?pub_id=${pub.id}`).then((r) => setVibe(r.data)).catch(() => {});
    }
  }, [pub]));

  const vote = async (optionId) => {
    try {
      const { data } = await api.post("/vibe/vote", { pub_id: pub.id, option_id: optionId });
      setVibe(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Vote failed");
    }
  };

  const resetShoutoutForm = () => {
    setMessage("");
    setDedicatedTo("");
    setSelectedPkg(null);
    setOccasion(null);
    setPayOpen(false);
    setPhotoFile(null);
    setPhotoPreview(null);
    setUploadedPhoto(null);
    setShoutoutSongText("");
  };

  const onPickPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Image too large (max 5MB)");
      return;
    }
    setPhotoFile(f);
    setPhotoPreview(URL.createObjectURL(f));
    setUploadingPhoto(true);
    try {
      const data = await uploadImage(f);
      setUploadedPhoto(data);
      toast.success("Photo ready");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
      setPhotoFile(null);
      setPhotoPreview(null);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const removePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    setUploadedPhoto(null);
  };

  const packageTier = (pkg) => {
    const n = (pkg?.name || "").toLowerCase();
    if (n.includes("gold")) return "gold";
    if (n.includes("silver")) return "silver";
    return "bronze";
  };

  const openShoutoutPay = () => {
    const tier = packageTier(selectedPkg);
    if (senderName.trim().length < 2) return toast.error("Enter your name");
    if (tier !== "bronze" && !uploadedPhoto) return toast.error("Please upload a photo");
    if (tier === "gold" && !shoutoutSongText.trim()) return toast.error("Type the song you want played");
    setPayOpen(true);
  };

  const shoutoutOrder = selectedPkg && {
    pub_id: pub?.id,
    purpose: "shoutout",
    shoutout: {
      package_id: selectedPkg.id,
      sender_name: senderName.trim(),
      occasion,
      message: message.trim() || null,
      dedication_to: dedicatedTo.trim() || null,
      table,
      photo_path: uploadedPhoto?.path || null,
      song_text: packageTier(selectedPkg) === "gold" ? shoutoutSongText.trim() : null,
    },
  };

  const onShoutoutPaid = () => {
    toast.success("Shout-out sent to the DJ!", { description: "Your payment is held until the DJ approves. Rejected = auto refund." });
    resetShoutoutForm();
  };

  if (loading || !pub) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0B0E14] text-white">
        <Loader2 className="h-6 w-6 animate-spin text-violet-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white">
      {/* Header */}
      <header
        className="sticky top-0 z-30 border-b border-white/5 backdrop-blur-xl"
        style={{ background: "rgba(11,14,20,0.75)" }}
      >
        <div className="mx-auto flex max-w-md items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <DhunLogo size={36} />
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-violet-300">
                {pub.name}
              </p>
              <p className="text-sm font-bold" data-testid="main-user-greeting">
                Hi, {user?.name}
              </p>
            </div>
            {table && (
              <span
                className="ml-1 rounded-full border border-amber-400/40 bg-amber-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-200"
                data-testid="main-table-badge"
              >
                Table {table}
              </span>
            )}
          </div>
          <button
            onClick={() => {
              logout();
              navigate(`/pub/${slug}`);
            }}
            data-testid="main-logout-button"
            className="rounded-full p-2 text-slate-400 hover:bg-white/5 hover:text-white"
            aria-label="Logout"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-md px-4 pb-28 pt-5">
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList
            className="sticky top-16 z-20 grid w-full grid-cols-2 border border-white/10 bg-[#131822] p-1"
            data-testid="main-tabs"
          >
            <TabsTrigger
              value="shoutouts"
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-violet-500 data-[state=active]:to-pink-500 data-[state=active]:text-white"
              data-testid="tab-shoutouts"
            >
              <Megaphone className="mr-1.5 h-4 w-4" /> Shout-out
            </TabsTrigger>
            <TabsTrigger
              value="vibe"
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-violet-500 data-[state=active]:to-pink-500 data-[state=active]:text-white"
              data-testid="tab-vibe"
            >
              <Flame className="mr-1.5 h-4 w-4" /> Vibe
            </TabsTrigger>
          </TabsList>


          {/* SHOUTOUTS */}
          <TabsContent value="shoutouts" className="mt-5">
            <p className="text-xs uppercase tracking-widest text-slate-400">
              Pick a dedication package
            </p>
            {packages.length === 0 && (
              <p className="mt-4 rounded-xl border border-dashed border-white/10 py-8 text-center text-sm text-slate-400" data-testid="shoutouts-unavailable">
                Shout-outs are unavailable right now
              </p>
            )}
            <div className="mt-3 grid gap-3">
              {packages.map((p) => (
                <button
                  key={p.id}
                  onClick={() => { setSelectedPkg(p); setOccasion(null); }}
                  data-testid={`shoutout-package-${p.name.toLowerCase().replace(/\s/g, "-")}`}
                  className={`relative overflow-hidden rounded-2xl border p-4 text-left transition ${
                    selectedPkg?.id === p.id
                      ? "border-violet-400 bg-violet-500/10 shadow-[0_0_28px_rgba(139,92,246,0.4)]"
                      : "border-white/10 bg-[#131822] hover:border-white/30"
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="flex items-center gap-1.5 text-base font-bold">
                        {p.name.toLowerCase().includes("gold") && (
                          <Crown className="h-4 w-4 text-amber-400" />
                        )}
                        {p.name}
                      </p>
                      <p className="mt-0.5 text-xs text-slate-400">{p.description}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-black text-white" data-testid={`shoutout-price-${p.tier}`}>₹{p.price}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {selectedPkg && !occasion && (
              <div className="mt-5 rounded-2xl border border-white/10 bg-[#131822] p-5" data-testid="occasion-picker">
                <p className="text-xs uppercase tracking-widest text-slate-400">What's the occasion?</p>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  {OCCASIONS.map((o) => (
                    <button key={o} onClick={() => setOccasion(o)} data-testid={`occasion-${o.toLowerCase().replace(/\s/g, "-")}`}
                      className="rounded-xl border border-white/10 bg-black/30 px-3 py-4 text-sm font-bold transition hover:border-violet-400 hover:bg-violet-500/10">
                      {o}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {selectedPkg && occasion && (
              <div className="mt-5 space-y-4 rounded-2xl border border-white/10 bg-[#131822] p-5">
                <div className="flex items-center justify-between">
                  <span className="rounded-full bg-violet-500/20 px-3 py-1 text-xs font-bold text-violet-200" data-testid="selected-occasion">{occasion}</span>
                  <button onClick={() => setOccasion(null)} className="text-xs text-slate-400 hover:text-white" data-testid="change-occasion">Change</button>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-widest text-slate-400">
                    Your name
                  </Label>
                  <Input
                    data-testid="shoutout-sender-input"
                    value={senderName}
                    onChange={(e) => setSenderName(e.target.value)}
                    className="h-11 border-white/10 bg-black/30 text-white"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-widest text-slate-400">
                    Dedicated to (optional)
                  </Label>
                  <Input
                    data-testid="shoutout-dedicated-input"
                    placeholder="e.g. The Birthday Girl 🎂"
                    value={dedicatedTo}
                    onChange={(e) => setDedicatedTo(e.target.value)}
                    className="h-11 border-white/10 bg-black/30 text-white"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-widest text-slate-400">
                    Your message (optional)
                  </Label>
                  <Textarea
                    data-testid="shoutout-message-input"
                    placeholder="Make it unforgettable..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={3}
                    className="border-white/10 bg-black/30 text-white"
                  />
                </div>

                {/* Silver / Gold: photo upload */}
                {(packageTier(selectedPkg) === "silver" || packageTier(selectedPkg) === "gold") && (
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-widest text-slate-400">
                      Your photo (required)
                    </Label>
                    {!photoPreview ? (
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        data-testid="shoutout-photo-upload"
                        className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-white/15 bg-black/20 p-6 text-sm text-slate-300 hover:border-violet-400 hover:text-white"
                      >
                        <ImagePlus className="h-4 w-4" /> Tap to add a photo
                      </button>
                    ) : (
                      <div className="relative">
                        <img
                          src={photoPreview}
                          alt="preview"
                          className="h-40 w-full rounded-xl object-cover"
                          data-testid="shoutout-photo-preview"
                        />
                        <button
                          type="button"
                          onClick={removePhoto}
                          data-testid="shoutout-photo-remove"
                          className="absolute right-2 top-2 rounded-full bg-black/70 p-1.5 text-white hover:bg-pink-500"
                        >
                          <XIcon className="h-3 w-3" />
                        </button>
                        {uploadingPhoto ? (
                          <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-black/60">
                            <Loader2 className="h-5 w-5 animate-spin text-white" />
                          </div>
                        ) : uploadedPhoto ? (
                          <span className="absolute bottom-2 left-2 inline-flex items-center gap-1 rounded-full bg-emerald-500/90 px-2 py-0.5 text-[10px] font-bold text-white">
                            <CheckCircle2 className="h-3 w-3" /> Ready
                          </span>
                        ) : null}
                      </div>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={onPickPhoto}
                      data-testid="shoutout-photo-file"
                    />
                  </div>
                )}

                {/* Gold: song name (free text) */}
                {packageTier(selectedPkg) === "gold" && (
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-widest text-amber-300">
                      Song you want played (required)
                    </Label>
                    <Input
                      value={shoutoutSongText}
                      onChange={(e) => setShoutoutSongText(e.target.value)}
                      placeholder="e.g. Kesariya — Arijit Singh"
                      data-testid="shoutout-song-text"
                      className="h-11 border-amber-400/40 bg-black/30 text-white"
                    />
                  </div>
                )}

                <Button
                  onClick={openShoutoutPay}
                  disabled={uploadingPhoto}
                  data-testid="shoutout-pay-button"
                  className="h-12 w-full rounded-full bg-gradient-to-r from-violet-500 to-pink-500 font-bold"
                >
                  Pay ₹{selectedPkg.price} & Send
                </Button>
              </div>
            )}

            <PaymentDialog
              open={payOpen}
              onOpenChange={setPayOpen}
              amount={selectedPkg?.price || 0}
              orderBody={shoutoutOrder}
              customerName={senderName}
              onSuccess={onShoutoutPaid}
              note="Held until the DJ approves. Rejected shout-outs are refunded automatically."
            />
          </TabsContent>

          {/* VIBE */}
          <TabsContent value="vibe" className="mt-5 space-y-5">
            <div className="rounded-2xl border border-white/10 bg-[#131822] p-5">
              <div className="mb-2 flex items-center justify-between">
                <p className="text-xs uppercase tracking-widest text-slate-400">Tonight's Vibe</p>
                {vibe.leader && (
                  <Badge
                    className="border-0 text-white"
                    style={{ background: vibe.leader.color, boxShadow: `0 0 15px ${vibe.leader.color}66` }}
                    data-testid="vibe-current-leader"
                  >
                    <Sparkles className="mr-1 h-3 w-3" /> Current Vibe: {vibe.leader.name}
                  </Badge>
                )}
              </div>
              <VibePieChart options={vibe.options} totalVotes={vibe.total_votes} />
            </div>

            <div className="grid grid-cols-2 gap-3">
              {vibe.options.map((o) => {
                const active = vibe.user_vote === o.id;
                return (
                  <button
                    key={o.id}
                    onClick={() => vote(o.id)}
                    data-testid={`vibe-vote-${o.name.toLowerCase().replace(/\s/g, "-")}`}
                    className={`relative overflow-hidden rounded-2xl border p-4 text-left transition ${
                      active
                        ? "border-transparent text-white"
                        : "border-white/10 bg-[#131822] text-white hover:border-white/30"
                    }`}
                    style={
                      active
                        ? {
                            background: `linear-gradient(135deg, ${o.color}dd, ${o.color}66)`,
                            boxShadow: `0 0 24px ${o.color}66`,
                          }
                        : {}
                    }
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className="h-3 w-3 rounded-full"
                        style={{ background: o.color, boxShadow: `0 0 10px ${o.color}` }}
                      />
                      <p className="text-sm font-bold">{o.name}</p>
                    </div>
                    <p className="mt-2 text-2xl font-black">{o.votes}</p>
                    <p className="text-xs text-white/70">{o.percent}% of votes</p>
                    {active && (
                      <span className="absolute right-2 top-2 rounded-full bg-black/40 px-2 py-0.5 text-[10px] font-bold">
                        Your pick
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            <p className="text-center text-xs text-slate-500">
              Tap again to remove your vote. Vibe voting is free.
            </p>
          </TabsContent>
        </Tabs>
        <AivigoFooter className="mt-10" />
      </div>

    </div>
  );
}
